import { forwardRef, useRef, useState, useEffect, useCallback, useImperativeHandle } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { PortalContainerContext } from "../context/PortalContainerContext";

/**
 * Enveloppe le plateau avec option plein écran et colonnes latérales.
 * ref.toggleFullscreen() / ref.isFullscreen pour contrôles externes.
 */
const BoardFullscreenShell = forwardRef(function BoardFullscreenShell(
  { children, className = "", leftAside = null, rightAside = null, hideToggle = false, onFullscreenChange },
  ref
) {
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [portalContainer, setPortalContainer] = useState(null);

  useEffect(() => {
    const onChange = () => {
      const fs = document.fullscreenElement === containerRef.current;
      setIsFullscreen(fs);
      setPortalContainer(fs ? containerRef.current : null);
      onFullscreenChange?.(fs);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, [onFullscreenChange]);

  const toggleFullscreen = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      } else if (document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch {
      /* navigateur a refusé */
    }
  }, []);

  useImperativeHandle(ref, () => ({ toggleFullscreen, isFullscreen }), [toggleFullscreen, isFullscreen]);

  const hasSidebars = Boolean(leftAside || rightAside);

  return (
    <PortalContainerContext.Provider value={portalContainer}>
    <div
      ref={containerRef}
      className={[
        "relative",
        isFullscreen
          ? "bg-tribal flex flex-col items-center justify-center min-h-screen w-full p-3 sm:p-4 overflow-y-auto"
          : "",
        className,
      ].join(" ")}
    >
      {!hideToggle && !rightAside && (
        <button
          type="button"
          onClick={toggleFullscreen}
          className={[
            "z-30 px-3 py-2 rounded-md border border-[color:var(--border)]",
            "bg-[color:var(--surface-glass)] backdrop-blur-md text-xs font-medium",
            "flex items-center gap-2 hover:bg-[color:var(--surface)] transition shadow-lg",
            isFullscreen ? "fixed top-3 right-3 sm:top-4 sm:right-4" : "absolute top-0 right-0",
          ].join(" ")}
          title={isFullscreen ? "Quitter le plein écran (Échap)" : "Afficher le plateau en plein écran"}
          data-testid="board-fullscreen-toggle"
        >
          {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          {isFullscreen ? "Réduire" : "Plein écran"}
        </button>
      )}

      <div
        className={[
          "w-full mx-auto",
          hasSidebars
            ? [
                "grid gap-3 sm:gap-4 items-start justify-center",
                isFullscreen
                  ? "max-w-[min(98vw,1100px)] grid-cols-1 lg:grid-cols-[minmax(140px,180px)_1fr_minmax(140px,180px)]"
                  : "max-w-5xl grid-cols-1 lg:grid-cols-[minmax(140px,180px)_1fr_minmax(140px,180px)]",
              ].join(" ")
            : isFullscreen
              ? "max-w-[min(94vmin,780px)] flex flex-col gap-3 sm:gap-4 [&_[data-testid=game-board]]:max-w-none"
              : "",
        ].join(" ")}
      >
        {leftAside && (
          <aside className="w-full lg:sticky lg:top-4 order-2 lg:order-1" data-testid="game-left-panel">
            {leftAside}
          </aside>
        )}

        <div
          className={[
            "w-full min-w-0 flex flex-col items-center",
            hasSidebars ? "order-1 lg:order-2" : "",
            isFullscreen && hasSidebars ? "[&_[data-testid=game-board]]:max-w-none" : "",
          ].join(" ")}
        >
          {children}
        </div>

        {rightAside && (
          <aside className="w-full lg:sticky lg:top-4 order-3" data-testid="game-right-panel">
            {rightAside}
          </aside>
        )}
      </div>
    </div>
    </PortalContainerContext.Provider>
  );
});

export default BoardFullscreenShell;
